// Import necessary dependencies
import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../../../utils/apiClient';
import { Dropdown, Menu, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { EyeOutlined, EditOutlined, DownloadOutlined } from '@ant-design/icons';
import { TGDataTable } from '../../../components/common';
import SubmissionDateFilterBar from './SubmissionDateFilterBar';

const ImpactAssessmentList = () => {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [dateFilter, setDateFilter] = useState(null);

  const handleDownload = async (formId, format) => {
    try {
      console.log(`Downloading ${format} for form ${formId}`);
      const response = await api.get(
        `/api/impact-assessment/download/${formId}/${format}`,
        {
          responseType: 'blob',
          headers: {
            'Accept': 'application/octet-stream',
          }
        }
      );

      const contentType = format === 'docx'
        ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        : 'application/pdf';

      const blob = new Blob([response.data], { type: contentType });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `impact_assessment_${formId}.${format}`);
      document.body.appendChild(link);
      link.click();

      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading assessment:', error);
      if (error.response) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result);
            console.error('Error details:', errorData);
          } catch (e) {
            console.error('Error parsing error response:', e);
          }
        };
        reader.readAsText(error.response.data);
      }
    }
  };

  // Column definitions for TGDataTable
  const columns = [
    {
      key: 'form_id',
      title: 'Form ID',
      dataIndex: 'form_id',
      width: 250,
      fixed: 'left',
      render: (text) => <span style={{ fontFamily: 'Monaco, monospace', fontSize: 11 }}>{text}</span>,
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      key: 'system_name',
      title: 'System Name',
      dataIndex: 'system_name',
      width: 200,
      sortable: true,
      filterable: true,
      filterType: 'text',
      ellipsis: true,
    },
    {
      key: 'submitted_at',
      title: 'Submission Date',
      dataIndex: 'submitted_at',
      width: 200,
      render: (text) => text ? new Date(text).toLocaleString() : '',
      sortable: true,
    },
    {
      key: 'actions',
      title: 'Actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/ai-ml/impact-assessment/view/${record.form_id}`)}
          >
            View
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => navigate(`/ai-ml/impact-assessment/edit/${record.form_id}`)}
          >
            Edit
          </Button>
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item key="word" icon={<DownloadOutlined />} onClick={() => handleDownload(record.form_id, 'docx')}>
                  Word
                </Menu.Item>
                <Menu.Item key="pdf" icon={<DownloadOutlined />} onClick={() => handleDownload(record.form_id, 'pdf')}>
                  PDF
                </Menu.Item>
              </Menu>
            }
            trigger={['click']}
          >
            <Button size="small" icon={<DownloadOutlined />}>
              Download
            </Button>
          </Dropdown>
        </div>
      ),
    },
  ];

  useEffect(() => {
    const fetchAssessments = async () => {
      try {
        const response = await api.get('/impact-assessment/list');
        console.log('Fetched assessments:', response.data);
        setAssessments(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching assessments:', error);
        if (error.response) {
          console.error('Response status:', error.response.status);
          console.error('Response data:', error.response.data);
        }
        setLoading(false);
      }
    };
    fetchAssessments();
  }, []);

  // Filter assessments by submission date
  const filteredAssessments = useMemo(() => {
    if (!dateFilter || !dateFilter.date) return assessments;

    const { operator, date } = dateFilter;
    const filterTime = new Date(date).setHours(0, 0, 0, 0);

    return assessments.filter((assessment) => {
      if (!assessment.submitted_at) return false;
      const submissionTime = new Date(assessment.submitted_at).setHours(0, 0, 0, 0);

      switch (operator) {
        case 'on':
          return submissionTime === filterTime;
        case 'after':
          return submissionTime > filterTime;
        case 'before':
          return submissionTime < filterTime;
        default:
          return true;
      }
    });
  }, [assessments, dateFilter]);

  // Clear all filters
  const handleClearFilters = () => {
    setDateFilter(null);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '16px' }}>
        <Button
          type="primary"
          onClick={() => navigate('/ai-ml/impact-assessment/new')}
          size="large"
        >
          Create New Assessment
        </Button>
      </div>

      <TGDataTable
        data={filteredAssessments}
        columns={columns}
        loading={loading}
        rowKey="form_id"
        pagination="client"
        pageSize={10}
        showExport
        showColumnConfig
        exportFilename="impact_assessments"
        presetKey="aiml.impact_assessments"
        externalFilterCount={dateFilter && dateFilter.date ? 1 : 0}
        onClearExternalFilters={handleClearFilters}
        toolbar={
          <SubmissionDateFilterBar
            dateFilter={dateFilter}
            onDateFilterChange={setDateFilter}
          />
        }
      />
    </div>
  );
};

export default ImpactAssessmentList;
